// Borrow card maths — pure, no I/O.
//
// The broker facts are DAILY (symbols.borrow, swept at 04:00 ET). Every number
// below is LIVE: it is recomputed from the current quote on every tick. Nothing
// here may be persisted — a stored feeBps is yesterday's feeBps.
//
// When the short sizing clamp lands in the worker it must produce the same
// `base` this file shows, or the card is lying about what a flip would do.
// Keep the cap set and the ordering here as the single description of the rule.
//
// Spec: architecture/systems/shorting/borrow-service.md + scope.md ("Sizing with a locate")

import { BUCKETS, baseShares, DEFAULT_SIZE_PARAMS, type SizeParams } from './constants';

export type BorrowState =
  | 'EASY_TO_BORROW' | 'LOCATABLE' | 'INSUFFICIENT_INVENTORY'
  | 'NO_INVENTORY' | 'THRESHOLD_SECURITY' | 'UNKNOWN';

export const STATE_META: Record<BorrowState, { label: string; verdict: string; tone: string }> = {
  EASY_TO_BORROW: { label: 'Easy to borrow', verdict: 'GO', tone: 'ok' },
  LOCATABLE: { label: 'Locatable', verdict: 'LOCATE', tone: 'warn' },
  INSUFFICIENT_INVENTORY: { label: 'Partial fill', verdict: 'PARTIAL', tone: 'warn2' },
  NO_INVENTORY: { label: 'No inventory', verdict: 'NO BORROW', tone: 'bad' },
  THRESHOLD_SECURITY: { label: 'Threshold list', verdict: 'BLOCKED', tone: 'bad' },
  UNKNOWN: { label: 'No quote', verdict: 'UNKNOWN', tone: 'bad' },
};

export interface BorrowFacts {
  state: BorrowState;
  status: string;
  shortable: boolean;
  availableQty: number | null;
  feePerShare: number | null;
  marginRequirementPct: number | null;
  roundLot: number;
  errorCode: string | null;
  errorMessage: string | null;
  attributes: string[];
  quotedAt: string;
}

export interface DailyInputs {
  sessionLow: number | null;
  prevClose: number | null;
  priorDayLow: number | null;
  prevPrevClose: number | null;
  ssrTriggerRatio: number;
  /** Regular-hours high-low, in dollars. Context for the break-even. */
  sessionRange?: number | null;
  priorDayRange?: number | null;
}

export interface AccountInputs {
  equity: number | null;
  buyingPower: number | null;
  regtBuyingPower: number | null;
  shortingEnabled: boolean | null;
}

export interface Quote { bid: number | null; ask: number | null; last: number | null }

/**
 * MIRRORS `snapToLocateLots` in src/core/short-sizing.ts — see the reasoning
 * there. A short that needs a locate snaps to the nearest size that uses every
 * located share (held part-lot + whole lots), so no borrow is paid for and left
 * idle (CLRO 2026-10-26: 300 located behind a 269-share short).
 */
function snapToLocateLots(want0: number, held0: number, buyableQty: number, capMargin: number | null, roundLot: number) {
  const lotSize = roundLot > 0 ? roundLot : 100;
  const held = Math.max(0, Math.floor(held0));
  const want = Math.max(0, Math.floor(want0));
  if (want <= 0) return { shares: 0, locateQty: 0, limitedBy: null as string | null };
  const odd = held % lotSize;
  const minLots = odd > 0 ? 0 : 1;
  const maxBySupply = Math.floor((held - odd) / lotSize) + Math.floor(Math.max(0, buyableQty) / lotSize);
  const maxByMargin = capMargin == null ? Number.MAX_SAFE_INTEGER : Math.floor((capMargin - odd) / lotSize);
  let lots = Math.max(minLots, Math.round((want - odd) / lotSize));
  let limitedBy: string | null = null;
  if (lots > maxBySupply) { lots = maxBySupply; limitedBy = 'available'; }
  if (lots > maxByMargin) { lots = maxByMargin; limitedBy = 'margin'; }
  if (lots >= minLots) {
    const shares = odd + lots * lotSize;
    if (shares < held) return { shares: want, locateQty: 0, limitedBy: null as string | null };
    return { shares, locateQty: shares - held, limitedBy };
  }
  if (maxBySupply < minLots) return { shares: 0, locateQty: 0, limitedBy: 'available' };
  return { shares: want, locateQty: want <= held ? 0 : lotSize, limitedBy: 'margin' };
}

/**
 * Short margin per share. Alpaca's per-ASSET `margin_requirement_short` when the
 * sweep captured it, else the standard rule. Either way a per-share floor
 * applies — $2.50 under $5, $5.00 at or above — and it is what makes cheap
 * stocks capital-expensive: a $0.35 name ties up ~7x its notional.
 */
export function marginPerShare(price: number, requirementPct: number | null, etb: boolean) {
  const pct = requirementPct ?? (etb && price >= 5 ? 50 : 100);
  const floor = price < 5 ? 2.5 : 5;
  const byPct = price * (pct / 100);
  const perShare = Math.max(byPct, floor);
  return { requirementPct: pct, perShare: +perShare.toFixed(4), rule: perShare > byPct ? 'per-share floor' : 'percent' };
}

/**
 * Reg SHO Rule 201. Triggered by a regular-hours print 10% below the prior
 * official close — and it LATCHES: in force for the rest of that day and all of
 * the next session. So a name that dropped 12% at 10:00 and recovered by noon is
 * still restricted, and yesterday's trigger still binds today. Comparing the
 * live price to the trigger (as a naive check does) gets both cases wrong.
 *
 * Derived, not authoritative — the exchanges publish the real list. Badge only.
 */
export function ssrStatus(d: DailyInputs, last: number | null) {
  const ratio = d.ssrTriggerRatio || 0.9;
  const triggerPrice = d.prevClose != null ? +(d.prevClose * ratio).toFixed(4) : null;
  const today = d.sessionLow != null && triggerPrice != null && d.sessionLow <= triggerPrice;
  const carried = d.priorDayLow != null && d.prevPrevClose != null && d.priorDayLow <= d.prevPrevClose * ratio;
  const known = d.prevClose != null;
  return {
    active: today || carried,
    known,
    triggerPrice,
    reason: today ? 'triggered today' : carried ? 'carried from prior session' : null,
    distancePct: triggerPrice != null && last != null && triggerPrice > 0
      ? +(((last - triggerPrice) / triggerPrice) * 100).toFixed(1) : null,
  };
}

export interface PresetRow {
  key: string; name: string; mult: number;
  shares: number; notional: number;
  locateCost: number | null; margin: number;
  binding: string | null;
}

/**
 * Derive everything the card renders. `facts` is daily; `quote` is live.
 *
 * Cap order matters. The UI multiplier scales the risk/value size exactly as
 * the worker's calculatePositionSize does; margin and inventory are then hard
 * ceilings on top. Locates are sold in round lots, so a short that needs one
 * snaps to the nearest size that uses every located share — and a result of
 * zero is a REFUSAL, never rounded up to minPositionSize the way the long path
 * does (PPCB quotes 61 shares; flooring up puts a 100-share order against 61).
 *
 * `heldLocateQty` is the borrow already held and not yet shorted against
 * (`/api/locates` remaining). It is sized on exactly as the worker does: it
 * outranks a blocked state, and a part-lot of it is used before more is bought.
 */
export function deriveBorrowFrame(opts: {
  symbol: string;
  facts: BorrowFacts;
  daily: DailyInputs;
  account: AccountInputs;
  params?: Partial<SizeParams> | null;
  quote: Quote;
  heldLocateQty?: number | null;
}) {
  const { symbol, facts, daily, account, quote } = opts;
  const p: SizeParams = { ...DEFAULT_SIZE_PARAMS, ...Object.fromEntries(Object.entries(opts.params ?? {}).filter(([, v]) => v != null)) } as SizeParams;

  const last = quote.last ?? quote.ask ?? quote.bid ?? null;
  const price = last && last > 0 ? last : 0;
  const prec = price > 0 && price < 1 ? 4 : 2;
  const meta = STATE_META[facts.state] ?? STATE_META.UNKNOWN;
  const etb = facts.state === 'EASY_TO_BORROW';
  const stateBlocked = facts.state === 'NO_INVENTORY' || facts.state === 'THRESHOLD_SECURITY' || facts.state === 'UNKNOWN';
  // Borrow in hand outranks a blocked state, as in the worker (DCOY 2026-09-22).
  const held = Math.max(0, Number(opts.heldLocateQty ?? 0) || 0);
  const blocked = stateBlocked && held <= 0;
  const roundLot = facts.roundLot || 100;
  const needsLocate = !etb;

  // ---- live market ----
  const bid = quote.bid ?? null, ask = quote.ask ?? null;
  const spread = bid != null && ask != null ? Math.max(0, ask - bid) : null;
  const changePct = daily.prevClose && daily.prevClose > 0 && price > 0
    ? +(((price - daily.prevClose) / daily.prevClose) * 100).toFixed(1) : null;

  // ---- cost, in DOLLARS, per round lot ----
  // Locates are sold in lots of 100, so the lot is the unit the operator
  // actually buys. Basis points are deliberately absent: "278 bps" is not a
  // thing you can hold in your head next to a $0.90 stock, whereas "$2.52 per
  // 100 shares, needs $0.031 down" is.
  const feePerShare = facts.feePerShare ?? null;
  const costPerShare = +(((feePerShare ?? 0) + (spread ?? 0)).toFixed(4));
  const perLot = {
    lot: roundLot,
    fee: feePerShare == null ? null : +(feePerShare * roundLot).toFixed(2),
    spread: spread == null ? null : +(spread * roundLot).toFixed(2),
    total: +(costPerShare * roundLot).toFixed(2),
    margin: null as number | null, // filled once margin is known
  };
  // You short at the bid, so that is what the break-even is measured from.
  const entryRef = bid ?? price ?? null;
  const breakEvenPrice = entryRef != null && entryRef > 0 ? +(entryRef - costPerShare).toFixed(4) : null;
  // How far it must fall, against how far it actually travels.
  const range = daily.sessionRange ?? daily.priorDayRange ?? null;
  const rangeIsToday = daily.sessionRange != null;
  const rangeMultiple = range != null && costPerShare > 0 ? +(range / costPerShare).toFixed(1) : null;

  // ---- margin + caps ----
  const margin = marginPerShare(price, facts.marginRequirementPct, etb);
  perLot.margin = +(margin.perShare * roundLot).toFixed(2);
  // An UNKNOWN buying power is not an unlimited one: returning null here removes
  // the margin ceiling and the card then shows a size it cannot justify. 0 is the
  // honest answer, and it matches the worker's refusal.
  const capFrom = (bp: number | null) => (margin.perShare > 0 ? (bp == null ? 0 : Math.floor(bp / margin.perShare)) : null);
  const capMargin = capFrom(account.buyingPower);
  const capMarginOvernight = capFrom(account.regtBuyingPower);
  const capAvailable = etb ? null : Math.max(facts.availableQty ?? 0, held);
  const buyable = etb || stateBlocked ? 0 : Math.max(0, (capAvailable ?? 0) - held);
  const riskValueBase = price > 0 ? baseShares(price, p) : 0;

  const caps = {
    riskValue: riskValueBase,
    margin: capMargin,
    marginOvernight: capMarginOvernight,
    available: capAvailable,
  };

  // MIRRORS src/core/short-sizing.ts. The two must agree share for share — this
  // card is the number the operator reads before pressing, and the worker is the
  // number that gets placed.
  //
  // The caps first, then — for a short that needs a locate — the lot snap, once,
  // last. A preset is a fraction of the UNSNAPPED base.
  const clamp = (want: number): { shares: number; binding: string | null } => {
    let shares = want, binding: string | null = want > 0 ? 'risk/value' : null;
    if (capMargin != null && capMargin < shares) { shares = capMargin; binding = 'margin'; }
    if (capAvailable != null && capAvailable < shares) { shares = capAvailable; binding = 'available'; }
    return { shares, binding };
  };
  const finish = (r: { shares: number; binding: string | null }): { shares: number; binding: string | null; locateQty: number } => {
    if (r.shares <= 0) return { shares: 0, locateQty: 0, binding: capAvailable != null && capAvailable < roundLot ? 'available' : r.binding };
    if (!needsLocate) return { shares: r.shares, binding: r.binding, locateQty: 0 };
    const s = snapToLocateLots(r.shares, held, buyable, capMargin, roundLot);
    if (s.shares <= 0) return { shares: 0, locateQty: 0, binding: 'available' };
    return { shares: s.shares, binding: s.limitedBy ?? r.binding, locateQty: s.locateQty };
  };

  const baseRaw = blocked ? { shares: 0, binding: 'available' as string | null } : clamp(riskValueBase);
  const base = blocked ? { shares: 0, binding: 'available' as string | null, locateQty: 0 } : finish(baseRaw);

  // A preset is a fraction of the CAPPED base, then re-clamped — not the
  // multiplier fed into the risk/value figure before capping. Done the other way
  // round, every preset collapses onto the same number whenever a cap binds, and
  // margin binds by design on sub-$5 names. scope.md: "Full = base, Half =
  // base/2, Quarter = base/4". Re-clamping is what stops 2x walking back through
  // the ceiling the base already respected.
  const presets: PresetRow[] = BUCKETS.map((b) => {
    const want = blocked ? 0 : Math.floor(baseRaw.shares * b.mult);
    const rc = blocked ? { shares: 0, binding: null as string | null } : clamp(want);
    // Re-clamping a figure already derived from the capped base usually does not
    // bind again — but the constraint worth naming is still the one that shaped
    // the base. Without this, Full reports 'risk/value' on a symbol whose size is
    // actually limited by inventory.
    const named = !blocked && rc.shares === want && baseRaw.binding ? baseRaw.binding : rc.binding;
    const r = blocked ? { shares: 0, binding: null as string | null, locateQty: 0 } : finish({ shares: rc.shares, binding: named });
    const shares = r.shares;
    const binding = r.binding;
    return {
      key: b.key, name: b.name, mult: b.mult,
      shares,
      notional: +(shares * price).toFixed(2),
      // Priced on the borrow this short BUYS — whole lots, net of what is
      // already held. shares × fee once showed $1.24 where the real ticket was
      // $2.30 (JLHL); a holding that covers the size costs nothing more.
      locateCost: facts.feePerShare == null || !needsLocate ? null
        : +((r.locateQty ?? 0) * facts.feePerShare).toFixed(2),
      margin: +(shares * margin.perShare).toFixed(2),
      binding,
    };
  });

  const ssr = ssrStatus(daily, price || null);

  return {
    symbol, prec,
    state: facts.state, stateLabel: meta.label, verdict: meta.verdict, verdictTone: meta.tone,
    borrow: {
      status: facts.status, shortable: facts.shortable, availableQty: facts.availableQty,
      feePerShare: facts.feePerShare, roundLot,
      errorCode: facts.errorCode, errorMessage: facts.errorMessage,
      quotedAt: facts.quotedAt, attributes: facts.attributes ?? [],
    },
    market: { bid, ask, last: price || null, spread, prevClose: daily.prevClose, changePct },
    ssr,
    margin,
    sizing: {
      caps, binding: base.binding, baseShares: base.shares,
      // Lots of BORROW to buy, not lots of position: the position is exact and
      // the locate is what rounds. base.shares/roundLot was a fraction the moment
      // the position stopped being lot-floored.
      locateQty: base.locateQty,
      lots: roundLot > 0 ? Math.ceil(base.locateQty / roundLot) : 0,
      presets, roundLot, needsLocate
    },
    decision: {
      perLot,
      costPerShare,
      breakEvenPrice,
      // Kept as a small secondary only — it is the one figure that compares
      // across symbols. Everything the operator acts on is in dollars.
      breakEvenPct: entryRef && entryRef > 0 ? +((costPerShare / entryRef) * 100).toFixed(2) : null,
      range, rangeIsToday, rangeMultiple,
      canShort: !blocked && base.shares > 0 && account.shortingEnabled !== false,
    },
    account,
  };
}

export type BorrowFrame = ReturnType<typeof deriveBorrowFrame>;
